/**
 * Visualizer — the syqel.art player. Pinned to the drive by default; the Director's journey is
 * opt-in. Audio lives in the app-wide audioSession (it survives navigation); entitlement is
 * verified against /api/status (signed cookie backed by Stripe); the SYQEL® watermark is drawn
 * into the scene pixels by the render pipeline for as long as the viewer isn't subscribed.
 */
import React, { useEffect, useRef, useState } from 'react'
import type { Director, DirectorState, Membership, MusicalFrame, World } from '@syqel/engine'
import { Engine, checkForUpdatesNow, isNewWorld, otaModeFactory, otaAutoEnabled, setOtaAuto as persistOtaAuto } from '@syqel/ota-client'
import {
  DEFAULT_WORLD_ID, FAVORITES_FILTER, VOLUMES, defaultWorld, favoriteWorlds, favoritesJourneyPool, galleryWorlds, offeredVolumes, onOfferChange, volumeOfWorld, volumeWorlds, journeyPool, worldAvailable, desktopTokenLink, isSubscribed, openExternal, refreshEntitlement, setEntToken, toggleFullscreen
} from './catalogue'
import { favorites } from './favorites'
import { createWatermark } from './watermark'
import { track, trackPurchase } from './analytics'
import { captureInput, captureNative, listInputs, loadFile, loadStems, onSource, sourceLabel } from './audioSession'
import { stereo } from './audio/graph'
import { bindHearing } from './audio/hearing'
import { createOrbControl } from './orbs'
import { onSeparation, readStems, type SeparationStatus } from './audio/stems'
import { isNativeAudio } from './audio/desktop'
import { castPlayoutSample } from './cast/session'
import { setCastCalm, setCastExposure, setCastReactivity, setCastScene } from './cast/sender'
import { TvCastPicker } from './TvCastPicker'
import { completeClaimIfPresent } from './firebaseClaim'
import { SourcePicker } from './SourcePicker'
import { SubscribeModal } from './SubscribeModal'
import { Celebration } from './Celebration'
import { SENS_DEFAULT, reactivityOf } from './reactivity'
import { createWorldGovernor } from './receiver/governor'
import { createPauseWatch } from './pauseWatch'
import { castTargetName, castTargetUnavailable, onCastTargetChange } from './cast/castTarget'
import { directorHeading, repoolDirector } from './directorPool'
import { worldNotes } from './worldNotes'
import { withLifecycle } from './lifecycle'
import { LifecycleLedger } from './renderPolicy'
import { createNativeSizer } from './nativeSurface'
import { recordSmoothness } from './worldCost'

const FLAT: Membership = { mainstream: 0.2, acoustic: 0.2, bassGroove: 0.2, mellow: 0.2, extreme: 0.2 }

// the sensitivity slider → reactivity mapping is shared with the phone shells: reactivity.ts
const SENS_KEY = 'syqel.sens' // per viewer (this browser / app install)
function savedSens(): number {
  try {
    const v = Number(localStorage.getItem(SENS_KEY))
    return v >= 0.1 && v <= 0.9 ? v : SENS_DEFAULT
  } catch { return SENS_DEFAULT }
}

// BRIGHTNESS — the host's output exposure in EV stops (VisualHost.setExposure, −2..+1, 0 = as authored;
// each fx visual is already normalized by its World.exposure, this is the viewer's taste on top). Free, per
// viewer (this browser / app install), and carried to the TV by a desktop cast (castProtocol 1.3).
const EXPOSURE_KEY = 'syqel.exposure'
function savedExposure(): number {
  try {
    const raw = localStorage.getItem(EXPOSURE_KEY)
    const v = raw === null ? 0 : Number(raw)
    return Number.isFinite(v) && v >= -2 && v <= 1 ? v : 0
  } catch { return 0 }
}
// CALM FLASHES — the photosensitivity switch (VisualHost.setCalm, engineApi 1.16): at most 3 flashes/s on the
// finished frame of every world, the fx family's reduce-motion. Free, per viewer, carried to a cast TV.
const CALM_KEY = 'syqel.calm'
function savedCalm(): boolean {
  try { return localStorage.getItem(CALM_KEY) === '1' } catch { return false }
}

// THE VOICE ORBS — "Add orbs that follow the voices" (free, per viewer, off by default): orbs.ts. On THIS screen
// only — a desktop cast carries no orb (owner: orbs never reach a TV).
const ORB_KEY = 'syqel.orb'
function savedOrb(): boolean {
  try { return localStorage.getItem(ORB_KEY) === '1' } catch { return false }
}

/** EV as the slider shows it: the light it lets through, 100% = as authored (−2 EV = 25%, +1 EV = 200%). */
const exposureLabel = (ev: number): string => `${Math.round(2 ** ev * 100)}%`

function pinnedState(world: World, intensity: number, density: number, richness: number): DirectorState {
  return {
    archetype: world.archetype, membership: FLAT, world, nextWorld: null, blend: 0,
    intensity, density, richness, buildArc: 0,
    render: Engine.worldRenderParams(world, intensity, density, richness)
  }
}

const btn: React.CSSProperties = {
  display: 'block', margin: '0 auto', padding: '12px 22px', fontSize: 14, fontFamily: 'monospace',
  color: '#fff', background: 'rgba(124,91,255,0.35)', border: '1px solid rgba(124,91,255,0.7)',
  borderRadius: 10, cursor: 'pointer'
}
const footLink: React.CSSProperties = { color: '#fff', opacity: 0.5, textDecoration: 'none' }

export function Visualizer(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loaded, setLoaded] = useState(sourceLabel())
  const [inputs, setInputs] = useState<MediaDeviceInfo[] | null>(null)
  const [worldLabel, setWorldLabel] = useState('—')
  // defaults: PINNED to the drive — the Director's journey is opt-in (owner: never auto-toggle)
  const [directed, setDirected] = useState(false)
  const [sceneId, setSceneId] = useState(() => {
    // ?world=<id> pins the PLAYER too (not just the gallery focus) — and it looks up the
    // FULL library, so unreleased next-volume worlds are reachable by direct URL
    const q = new URLSearchParams(window.location.search).get('world')
    if (q && Engine.WORLDS.some((w) => w.id === q)) return q
    try {
      // a gallery click hands the world via sessionStorage. READ but don't remove here — a useState
      // initializer must be side-effect-free (StrictMode runs it twice; removing on the first pass
      // made the second pass miss it and fall back to the default). It's cleared in an effect below.
      const handed = sessionStorage.getItem('syqel.scene')
      if (handed) return handed
    } catch { /* private mode */ }
    return DEFAULT_WORLD_ID // the default visual on open (volumes.ts; the volume state derives from it)
  })
  // VOLUME: which world set the picker offers (a VOLUMES id) — the one holding the opening world — or
  // FAVORITES_FILTER: the user's starred worlds, which ALSO becomes the Director's pool (play just my
  // favorites). One state for both, so the picker and the journey can never disagree about the filter.
  const [volume, setVolume] = useState<string>(() => (volumeOfWorld(sceneId) ?? VOLUMES[0]).id)
  const favOnly = volume === FAVORITES_FILTER
  // re-render on every star/un-star (this tab's or another's) — the lists below derive from the store
  const [, setFavRev] = useState(0)
  useEffect(() => favorites.subscribe(() => setFavRev((n) => n + 1)), [])
  // ...and on every offer change (entitlement, the extended fx visuals going reachable / unreachable):
  // the volume and world selects list only what this player can load right now
  useEffect(() => onOfferChange(() => setFavRev((n) => n + 1)), [])
  // ...and on the pushed TV's offer (cast/castTarget.ts): the world select marks what that TV can't show
  useEffect(() => onCastTargetChange(() => setFavRev((n) => n + 1)), [])
  const favList = favoriteWorlds()
  const pickerWorlds = (() => {
    const list = galleryWorlds(volume)
    // un-starring the world on screen while browsing favorites: keep it listed until the user moves on,
    // so the select keeps showing what is actually playing
    if (!favOnly || list.some((w) => w.id === sceneId)) return list
    const current = Engine.WORLDS.find((w) => w.id === sceneId)
    return current ? [current, ...list] : list
  })()
  // the world on screen right now (the Director's, or the pinned one) — what the ☆/★ toggle acts on
  const [currentWorldId, setCurrentWorldId] = useState(sceneId)
  const leaveFavorites = (): void => setVolume((volumeOfWorld(sceneRef.current) ?? VOLUMES[0]).id)
  const [subReason, setSubReason] = useState<null | 'support' | 'periodic' | 'premium'>(null)
  // the periodic ask: every 3.5 min of free playback the modal rises (always refusable)
  const nextAskRef = useRef(210)
  const [party, setParty] = useState(false)
  const [subNotice, setSubNotice] = useState<string | null>(null)
  const [controlsOn, setControlsOn] = useState(true)
  const directedRef = useRef(directed)
  const sceneRef = useRef(sceneId)
  const loadedRef = useRef(loaded !== '')
  const hoverRef = useRef(false)
  const lastActivityRef = useRef(0)
  const [sens, setSens] = useState(savedSens)
  const [exposure, setExposure] = useState(savedExposure)
  const exposureRef = useRef(exposure)
  const [calm, setCalm] = useState(savedCalm)
  const calmRef = useRef(calm)
  const [orb, setOrb] = useState(savedOrb)
  const orbRef = useRef(orb)
  // OTA world updates: auto-download by default; manual mode gets a "check now" action
  const [otaAuto, setOtaAutoState] = useState(otaAutoEnabled)
  const [otaMsg, setOtaMsg] = useState('')
  const sensRef = useRef(sens)
  const directorRef = useRef<Director | null>(null)
  const favOnlyRef = useRef(favOnly)
  // hand the running Director the pool for the current filter + entitlement + favorites (set by the
  // render effect, which owns the Director)
  const repoolRef = useRef<(() => void) | null>(null)
  const lastFrameRef = useRef<MusicalFrame | null>(null)
  const [training, setTraining] = useState(false)
  const trainingRef = useRef(false)
  const [trainToast, setTrainToast] = useState<string | null>(null)
  // "Not available on <TV>": picking a world the pushed TV can't show — this computer plays it, the TV keeps
  // its current visual (receiver/mirror.ts), and the user is told why instead of the TV seeming to ignore it
  const [castNotice, setCastNotice] = useState<string | null>(null)
  const castNoticeTimer = useRef<number | null>(null)
  // every user pick goes through this (the world select, a gallery tile handed over)
  const tellIfNotOnTv = (id: string): void => {
    const tv = castTargetName()
    if (!tv || !castTargetUnavailable(id)) return
    setCastNotice(`Not available on ${tv} — it keeps its current visual`)
    if (castNoticeTimer.current !== null) window.clearTimeout(castNoticeTimer.current)
    castNoticeTimer.current = window.setTimeout(() => setCastNotice(null), 4000)
  }
  const trainToastTimer = useRef<number | null>(null)
  // INSTRUMENT DRIVE toggle (Ctrl+Alt+D): drive the worlds from the lean source detector
  // (kick/bass/hats…) instead of the raw frequency voices. ON by default; flip to A/B.
  const detDriveRef = useRef(true)
  const prevNextRef = useRef<string | null>(null)

  useEffect(() => {
    sensRef.current = sens
    try { localStorage.setItem(SENS_KEY, String(sens)) } catch { /* private mode */ }
    // a desktop cast carries it to the TV (castProtocol 1.2) — the TV responds as this player does
    setCastReactivity(reactivityOf(sens))
  }, [sens])
  useEffect(() => {
    exposureRef.current = exposure
    try { localStorage.setItem(EXPOSURE_KEY, String(exposure)) } catch { /* private mode */ }
    setCastExposure(exposure) // a desktop cast shows the TV at this player's brightness (castProtocol 1.3)
  }, [exposure])
  useEffect(() => {
    calmRef.current = calm
    try { localStorage.setItem(CALM_KEY, calm ? '1' : '0') } catch { /* private mode */ }
    setCastCalm(calm) // a desktop cast calms the TV too (castProtocol 1.4)
  }, [calm])
  useEffect(() => {
    orbRef.current = orb
    try { localStorage.setItem(ORB_KEY, orb ? '1' : '0') } catch { /* private mode */ }
  }, [orb])
  useEffect(() => { trainingRef.current = training }, [training])
  useEffect(() => { favOnlyRef.current = favOnly; repoolRef.current?.() }, [favOnly])
  useEffect(() => { directedRef.current = directed; lastActivityRef.current = performance.now() }, [directed])
  useEffect(() => { sceneRef.current = sceneId; lastActivityRef.current = performance.now() }, [sceneId])
  useEffect(() => { loadedRef.current = loaded !== ''; lastActivityRef.current = performance.now() }, [loaded])
  useEffect(() => onSource(setLoaded), [])
  // consume the one-shot gallery hand-off after mount (cleared here, NOT in the sceneId initializer,
  // so StrictMode's double-invoke can't empty it before the value is read)
  // A world the gallery handed over is a pick like the select's: say so if the pushed TV can't show it.
  useEffect(() => {
    try {
      const handed = sessionStorage.getItem('syqel.scene')
      sessionStorage.removeItem('syqel.scene')
      if (handed) tellIfNotOnTv(handed)
    } catch { /* private mode */ }
  }, [])

  // SPACEBAR (in Director mode): fire a transition NOW — and log the analyzer's state at the
  // moment a human felt the music turn. The owner trains the phrase detector by triggering on
  // real arrangements; rows accumulate in localStorage 'syqel.train' for offline correlation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'Space' || !directedRef.current) return
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) return
      e.preventDefault()
      // TRAINING MODE: the mark is recorded but the auto Director is never touched —
      // its run stays clean, and we can compare its choices against the human's marks
      if (!trainingRef.current) directorRef.current?.requestTransition()
      const f = lastFrameRef.current
      if (f) {
        const row = {
          src: trainingRef.current ? 'human' : 'manual',
          t: Math.round(f.timeSec * 100) / 100,
          energy: Math.round(f.energy * 1000) / 1000,
          build: Math.round(f.buildArc * 100) / 100,
          cal: Object.fromEntries(Object.entries(f.cal).map(([k, v]) => [k, Math.round((v as number) * 100) / 100]))
        }
        console.log('[syqel·train]', JSON.stringify(row))
        try {
          const arr = JSON.parse(localStorage.getItem('syqel.train') ?? '[]') as unknown[]
          arr.push(row)
          while (arr.length > 800) arr.shift()
          localStorage.setItem('syqel.train', JSON.stringify(arr))
        } catch { /* private mode */ }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // SECRET TRAINING TOGGLE (owner-only, no UI): Ctrl+Alt+T flips training mode with a
  // small toast so the owner knows which side of it he's on — users never see a control
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey && e.altKey && e.code === 'KeyT')) return
      e.preventDefault()
      const next = !trainingRef.current
      setTraining(next)
      setTrainToast(next ? 'training mode on' : 'training mode off')
      if (trainToastTimer.current !== null) window.clearTimeout(trainToastTimer.current)
      trainToastTimer.current = window.setTimeout(() => setTrainToast(null), 2200)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // INSTRUMENT-DRIVE A/B (Ctrl+Alt+D): worlds driven by the lean source detector vs raw voices
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey && e.altKey && e.code === 'KeyD')) return
      e.preventDefault()
      detDriveRef.current = !detDriveRef.current
      setTrainToast(detDriveRef.current ? 'instrument drive ON (detected)' : 'instrument drive OFF (raw voices)')
      if (trainToastTimer.current !== null) window.clearTimeout(trainToastTimer.current)
      trainToastTimer.current = window.setTimeout(() => setTrainToast(null), 2200)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // entitlement: verify on arrival (and consume the Stripe return params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const sub = params.get('sub')
    // /api/activate appends the paid amount + transaction id so GA can record the purchase
    const paidCents = Number(params.get('v') ?? 0)
    const transactionId = params.get('tid') ?? ''
    // a desktop checkout returns here in the browser with the entitlement token to relay to the app
    const desktopReturn = params.get('desktop') === '1'
    const actToken = params.get('token') ?? ''
    if (sub) {
      params.delete('sub')
      params.delete('v')
      params.delete('tid')
      params.delete('desktop')
      params.delete('token')
      const qs = params.toString()
      history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`)
    }
    // hand the token to the desktop app — it unlocks without the user also claiming by email
    if (sub === 'ok' && desktopReturn && actToken) window.location.href = desktopTokenLink(actToken)
    void (async () => {
      // a Firebase sign-in link return? finish the claim before the entitlement check
      const claim = await completeClaimIfPresent()
      if (claim !== 'none') track('claim_complete', { result: claim })
      if (claim === 'claimed') {
        setParty(true)
      } else if (claim === 'no-customer') {
        setSubNotice('no SYQEL billing profile found for that email — your subscription may be under a different email')
        setSubReason('support')
      } else if (claim === 'no-qualifying-sub') {
        setSubNotice("found your billing profile, but no active paid subscription qualifies (Gig & free tiers don't include Art)")
        setSubReason('support')
      } else if (claim === 'fb-unconfigured' || claim === 'fb-lookup-failed') {
        setSubNotice(`account check unavailable (${claim}) — contact support@syqel.com`)
        setSubReason('support')
      } else if (claim === 'error') {
        setSubNotice('claim link expired or was opened on a different device — request a new one')
        setSubReason('support')
      }
      const ok = await refreshEntitlement()
      if (sub === 'ok' && paidCents > 0 && transactionId) trackPurchase(paidCents, transactionId)
      if (ok && sub === 'ok') setParty(true)
      if (!ok && sub === 'ok') setSubNotice('payment received — subscription is still settling, try again in a minute')
    })()
  }, [])

  // DESKTOP DEEP LINK (syqel-art://claim?token=…): an existing subscriber claims in the browser,
  // which hands the entitlement token back to the app here. Store it, re-validate, celebrate.
  useEffect(() => {
    if (!isNativeAudio()) return
    let unlisten: (() => void) | undefined
    const handle = (urls: string[] | string | null): void => {
      const list = Array.isArray(urls) ? urls : urls ? [urls] : []
      for (const u of list) {
        try {
          const url = new URL(u)
          if (url.protocol !== 'syqel-art:') continue
          const token = url.searchParams.get('token')
          if (!token) continue
          setEntToken(token)
          void refreshEntitlement().then((ok) => { if (ok) { setSubReason(null); setParty(true) } })
        } catch { /* not a url we handle */ }
      }
    }
    void (async () => {
      try {
        const dl = await import('@tauri-apps/plugin-deep-link')
        const launchedWith = await dl.getCurrent().catch(() => null) // app opened via the link
        if (launchedWith) handle(launchedWith)
        unlisten = await dl.onOpenUrl(handle) // while the app is already running
      } catch (e) {
        console.warn('[syqel] deep-link init failed', e)
      }
    })()
    return () => unlisten?.()
  }, [])

  // in-engine separation status chip (drop a file → instruments hot-swap in when ready)
  const [sep, setSep] = useState<SeparationStatus>({ state: 'idle' })
  useEffect(() => onSeparation(setSep), [])
  useEffect(() => {
    if (sep.state !== 'live') return
    const t = window.setTimeout(() => setSep({ state: 'idle' }), 5000)
    return () => window.clearTimeout(t)
  }, [sep.state])

  useEffect(() => {
    // detectSources: the ENGINE owns mix→sources detection (SDK 0.8) — it stamps frame.detected +
    // frame.sources inside update(); the app only overrides sources when real stems are live.
    // sensitivity stays the analyzer's default ONSET THRESHOLD (how many pan-events fire — analysis,
    // not taste); the slider is reactivity, the visible response (reactivityOf above)
    const music = new Engine.MusicalAnalyzer({ sensitivity: SENS_DEFAULT, reactivity: reactivityOf(sensRef.current), detectSources: true })
    const synth = new Engine.SyntheticMusic()
    // the world turns over ON the music: after a 22s minimum it cuts on the next section
    // (drop/breakdown/shift), with a 70s max-hold so steady songs still rotate (no overstaying).
    // free journeys Volume 1 + the free showcases; Pro adds the premium volumes (journeyPool reads
    // the entitlement). ENTITLEMENT CHANGES THE POOL, NOT THE PICTURE: it is verified (/api/status,
    // StoreKit, a claim deep link) AFTER this Director exists, so an entitlement change hands the
    // SAME Director a new pool (engineApi 1.12 setPool: the world on screen stays, a prewarm the new
    // pool excludes is dropped) and the new gating applies from the next pick. A new Director would
    // make its own opening pick = swap the visible world.
    // ★ FAVORITES: with the favorites filter on, the journey is the user's starred worlds — still gated
    // by entitlement (favoritesJourneyPool), and the same setPool rule: the filter, a star, or an
    // entitlement change hands the SAME Director a new pool; nothing rebuilds it.
    const currentPool = (): World[] => {
      const pool = favOnlyRef.current ? favoritesJourneyPool() : journeyPool()
      // casting: the Director picks only what the pushed TV can show (its offer — cast/castTarget.ts). Never
      // an empty pool: a TV that can show none of them leaves the pool as it was (each cut then says so on
      // the TV by keeping its visual).
      const onTv = pool.filter((w) => !castTargetUnavailable(w.id))
      return onTv.length ? onTv : pool
    }
    const director = new Engine.Director({ pool: currentPool(), minHoldSec: 22, maxHoldSec: 70, transitionSec: 3, phraseLock: true })
    directorRef.current = director
    // the Director's last state (null = not driving yet) — a pool change judges where it is headed
    // (directorPool.ts: entitlement REVOKED, the favorites filter, an un-star, the pushed TV can't show it)
    let directorState: DirectorState | null = null
    const repool = (): void => {
      repoolDirector(director, currentPool(), directedRef.current && directorState ? directorHeading(directorState) : null)
    }
    repoolRef.current = repool
    const stopEntitlementWatch = onOfferChange(repool)
    const stopFavoritesWatch = favorites.subscribe(() => { if (favOnlyRef.current) repool() })
    const stopCastWatch = onCastTargetChange(repool) // a new TV, a new offer, the cast ending
    // every renderer's construction + teardown, accounted per frame: the governor never charges a build to the
    // world it is measuring (lifecycle.ts)
    const ledger = new LifecycleLedger()
    const host = new Engine.VisualHost({
      modeFactory: withLifecycle(otaModeFactory, ledger),
      exposure: exposureRef.current,
      calm: calmRef.current,
      onModeError: (key, err) => console.warn(`[syqel] renderer '${key}' failed, using fallback:`, err)
    })
    // the scene a desktop cast mirrors on the TV (castProtocol 1.4) — told to the shell on change
    let lastSceneKey = ''
    const canvas = canvasRef.current
    if (!canvas) { stopEntitlementWatch(); stopFavoritesWatch(); stopCastWatch(); repoolRef.current = null; return }
    // the host owns the output canvas (PostFX takes WebGL on it; do NOT getContext here)
    host.attach(canvas)

    // THE RENDER POLICY (renderPolicy.ts, every surface's): always the NATIVE drawable — resolution is never a
    // lever; each world's detail is calibrated on this device (the highest that meets the desktop's smoothness
    // bar) and remembered (worldCost.ts). A world that misses the bar even at its minimum detail is heavy here
    // and leaves the Director's pools; the picker marks it. The 'syqel.adapt'='0' localStorage flag is an
    // escape hatch: authored detail, nothing calibrated.
    const adapt = (() => { try { return localStorage.getItem('syqel.adapt') !== '0' } catch { return true } })()
    const gov = adapt ? createWorldGovernor('desktop', host) : null
    const orbs = createOrbControl(host, 'desktop') // this screen's voice orbs (renderPolicy orbFor)
    const nativeSize = createNativeSizer(canvas)
    const pauses = createPauseWatch() // the page hidden or frozen across an interval: wall time, never a frame
    let lastSettled = false // the frame just drawn showed the governed world alone (its interval is the next realDt)

    let raf = 0
    let disposed = false
    let last = performance.now()
    let lastLabel = ''
    let playSeconds = 0
    const applyWatermark = createWatermark()
    // THE HEARING (frame.drivers — what fx and lite worlds react to): fed only while a renderer in play reads
    // drivers (VisualHost.wantsDrivers — on stage, incoming or prewarming, fallbacks excluded); it starts
    // on first use, off the first-render path, in a worker (audio/hearing.ts).
    const hearing = bindHearing(music, '[syqel]')

    const loop = (): void => {
      const now = performance.now()
      const realDt = (now - last) / 1000 // true frame time for the governor (dt below is capped for the sim)
      const dt = Math.min(0.05, realDt)
      last = now
      const t = now / 1000
      // the ledger is asked as the interval closes: a build, compile or teardown across it (any world's) is skipped
      const paused = pauses.spanned() // asked every frame: each call closes its interval
      gov?.tick(realDt, lastSettled && !paused, ledger.endFrame()) // detail only — the drawable is always native (renderPolicy.ts)
      const { w, h } = nativeSize()
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
      }

      music.setReactivity(reactivityOf(sensRef.current))
      hearing.feed() // in-thread fallback only: the tapped PCM since the last frame → exact 60 Hz student hops
      // attract mode: until audio connects, the drive runs on the synthetic groove behind the home UI
      const frame = loadedRef.current ? music.updateFromAnalyser(stereo, t) : synth.frame(t)
      if (loadedRef.current) {
        // frame.detected + frame.sources are stamped by the analyzer (detectSources: true).
        // STEM FUSION: when isolated instruments (true stems) are live, they REPLACE the frame's
        // voices + pan events (true instruments, true pans). Aggregate axes / build-arc /
        // sections still come from the mix analyzer above.
        const fusion = readStems(t)
        if (fusion) {
          frame.voices = fusion.voices
          frame.panEvents = fusion.panEvents
          frame.stems = fusion.stems
          // ★ SOURCE CONTRACT override: real stems win over the engine's DSP estimates
          // (provenance renders bold — solidity 1). No override → the engine's sources hold.
          frame.sources = Engine.sourcesFromStems(fusion.stems)
        } else if (detDriveRef.current && frame.detected && frame.detected.length) {
          // INSTRUMENT DRIVE: no true stems → drive the worlds from the engine's detector
          // (kick/bass/hats…) instead of raw frequency voices. Ctrl+Alt+D to A/B.
          const fused = Engine.fuseDetectedToVoices(frame.detected, t)
          frame.voices = fused.voices
          frame.panEvents = fused.panEvents
        }
      }
      lastFrameRef.current = frame
      let dir: DirectorState
      if (directedRef.current) {
        dir = director.update(frame, dt)
        directorState = dir
      } else {
        const world = Engine.WORLDS.find((x) => x.id === sceneRef.current) ?? defaultWorld()
        dir = pinnedState(world, frame.cal.energy, frame.cal.onsetDensity, Engine.richnessOf(frame.cal))
      }
      hearing.listen(loadedRef.current && host.wantsDrivers())
      // MIRRORING (desktop sender): the world on screen, whoever chose it — the Director's journey included,
      // told from the frame a cut starts (owner decision: the TV always shows what this player shows). The
      // shell owns the casts — it stamps the seq, re-bases the cut sample onto each TV's stream and replays
      // the latest to a TV that connects later; off the desktop this is a no-op.
      const mirrorWorld = (dir.nextWorld ?? dir.world).id
      const prewarm = dir.prewarmWorld && dir.prewarmWorld.id !== mirrorWorld ? dir.prewarmWorld.id : null
      const sceneKey = `${mirrorWorld}|${prewarm}`
      if (sceneKey !== lastSceneKey) {
        lastSceneKey = sceneKey
        const at = castPlayoutSample()
        setCastScene({ world: mirrorWorld, fxMode: null, prewarm, atSample: at == null ? null : Math.round(at) })
      }
      host.setExposure(exposureRef.current) // eased by the host — a slider drag glides
      host.setCalm(calmRef.current)
      orbs.apply(orbRef.current)
      host.update(frame, dir, dt, t, w, h)
      host.render()
      if (gov) {
        // a world settled on screen takes the governor (the one it left keeps its quality for a return)
        if (!dir.nextWorld) gov.settle(dir.world.id)
        lastSettled = !dir.nextWorld && !document.hidden && host.activeModeReady()
        if (!dir.nextWorld) recordSmoothness(dir.world.id, gov)
      }

      // log the Director's OWN transition moments (src:'director') for the correlation set
      const nextId = dir.nextWorld?.id ?? null
      if (nextId && prevNextRef.current !== nextId) {
        const drow = {
          src: 'director', world: nextId,
          t: Math.round(frame.timeSec * 100) / 100,
          energy: Math.round(frame.energy * 1000) / 1000,
          build: Math.round(frame.buildArc * 100) / 100,
          cal: Object.fromEntries(Object.entries(frame.cal).map(([k, vv]) => [k, Math.round((vv as number) * 100) / 100]))
        }
        console.log('[syqel·train]', JSON.stringify(drow))
        try {
          const arr = JSON.parse(localStorage.getItem('syqel.train') ?? '[]') as unknown[]
          arr.push(drow)
          while (arr.length > 800) arr.shift()
          localStorage.setItem('syqel.train', JSON.stringify(arr))
        } catch { /* private mode */ }
      }
      prevNextRef.current = nextId

      const label = dir.nextWorld ? `${dir.world.name} → ${dir.nextWorld.name}` : dir.world.name
      if (label !== lastLabel) {
        lastLabel = label
        setWorldLabel(label)
        setCurrentWorldId(dir.world.id)
      }

      if (loadedRef.current) {
        // NO HARD GATE (owner policy): free users watch as long as they like — the asks are
        // the watermark and a PERIODIC modal they can always refuse.
        if (!isSubscribed() && playSeconds >= nextAskRef.current) {
          nextAskRef.current = playSeconds + 210
          setSubReason('periodic')
        }
        // The SYQEL® mark: always on for non-subscribers (the shared rule — watermark.ts)
        playSeconds += dt
        applyWatermark(host, dt)
        // controls auto-hide after a few seconds; hovering their corner revives them
        const idle = (now - lastActivityRef.current) / 1000 > 4
        setControlsOn(!idle || hoverRef.current)
      } else {
        setControlsOn(true)
      }

      if (!disposed) raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => {
      disposed = true
      cancelAnimationFrame(raf)
      stopEntitlementWatch()
      stopFavoritesWatch()
      stopCastWatch()
      repoolRef.current = null
      orbs.dispose()
      hearing.dispose()
      pauses.dispose()
      host.dispose()
      // NOTE: the audio session is app-wide on purpose — navigation must not stop the music
    }
  }, [])

  const verifySubscribed = (): void => {
    setSubNotice(null)
    void refreshEntitlement().then((ok) => {
      if (ok) {
        setSubReason(null)
        setParty(true)
      } else {
        setSubNotice('no active subscription found for this browser — finish checkout, or use manage subscription')
      }
    })
  }

  const pickInput = async (): Promise<void> => {
    setInputs(await listInputs())
  }

  const wordmark: React.CSSProperties = { fontFamily: "'Changa', sans-serif", fontWeight: 700 }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#06060a' }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
      onDrop={(e) => {
        e.preventDefault()
        const files = Array.from(e.dataTransfer?.files ?? [])
        if (files.length > 1) void loadStems(files) // a folder of stems → isolated instruments
        else if (files[0]) loadFile(files[0])
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      {sep.state !== 'idle' && (
        <div style={{ position: 'absolute', right: 14, bottom: 14, maxWidth: 520, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', padding: '6px 12px', borderRadius: 8, background: 'rgba(8,10,18,0.82)', border: '1px solid rgba(255,255,255,0.14)', font: '11px monospace', color: sep.state === 'failed' ? '#ffb4b4' : '#cfe3ff', zIndex: 12 }}>
          {sep.state === 'working'
            ? `🎛 separating instruments… ${sep.pct ?? 0}% ${sep.phase ?? ''}`
            : sep.state === 'live'
              ? '🎼 instruments live'
              : `⚠ separation failed —\n${sep.phase ?? 'see console'}`}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 1) void loadStems(files)
          else if (files[0]) loadFile(files[0])
        }}
      />
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(4,4,10,0.35)' }}>
          <div style={{ textAlign: 'center', font: '14px monospace', color: '#fff' }}>
            <div style={{ ...wordmark, fontSize: 42, letterSpacing: 4, marginBottom: 2, opacity: 0.96 }}>
              SYQEL<span style={{ fontSize: 14, verticalAlign: 'super' }}>®</span> Art
            </div>
            <div style={{ fontSize: 12, marginBottom: 22, opacity: 0.45 }}>
              endless worlds for your music · created by{' '}
              <a href="https://yoavshalev.com/" target="_blank" rel="noreferrer" style={{ color: '#fff' }} onClick={(e) => { e.preventDefault(); openExternal('https://yoavshalev.com/') }}>Yoav Shalev</a>
            </div>
            {inputs === null ? (
              <>
                <button onClick={() => fileInputRef.current?.click()} style={btn}>📁 Choose / drop an audio or video file</button>
                <button onClick={() => { if (isNativeAudio()) void captureNative(); else void pickInput() }} style={{ ...btn, marginTop: 10, background: 'rgba(86,140,220,0.25)', border: '1px solid rgba(86,140,220,0.55)' }}>
                  🔊 {isNativeAudio() ? 'Connect visualizer to system audio or specific app' : 'Connect an input device (no share bar)'}
                </button>
                <button onClick={() => setSubReason('support')} style={{ ...btn, marginTop: 10, background: 'rgba(255,120,160,0.22)', border: '1px solid rgba(255,120,160,0.5)' }}>
                  🔥 Remove watermark
                </button>
              </>
            ) : (
              <div style={{ maxWidth: 360 }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                  pick an audio input — loopback devices (Stereo Mix, VB-Cable, a DJ interface) carry the system's sound with no share bar
                </div>
                {inputs.length === 0 && <div style={{ fontSize: 12, opacity: 0.6 }}>no input devices found</div>}
                {inputs.map((d) => (
                  <button
                    key={d.deviceId}
                    onClick={() => { void captureInput(d.deviceId, d.label || 'input device') }}
                    style={{ ...btn, marginTop: 8, fontSize: 12, padding: '9px 14px' }}
                  >
                    {d.label || `input ${d.deviceId.slice(0, 6)}…`}
                  </button>
                ))}
                <button onClick={() => setInputs(null)} style={{ ...btn, marginTop: 12, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', fontSize: 12 }}>
                  ← back
                </button>
              </div>
            )}
            <div style={{ marginTop: 16, opacity: 0.5, fontSize: 12 }}>the Director reads the music and journeys through worlds that tell its story</div>
            {!isNativeAudio() && (
              <div style={{ marginTop: 22 }}>
                <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 9 }}>
                  get the apps — phone, desktop, and your TV
                </div>
                <div style={{ display: 'flex', gap: 9, justifyContent: 'center', flexWrap: 'wrap', maxWidth: 460, margin: '0 auto' }}>
                  {[
                    // one universal Apple app: iPhone + Mac + Apple TV share the listing
                    { label: '🍎 App Store', sub: 'iPhone · Mac · Apple TV', url: 'https://apps.apple.com/us/app/syqel-art-music-visualizer/id6783419598' },
                    { label: '▶ Google Play', sub: 'Android · Android TV', url: 'https://play.google.com/store/apps/details?id=com.syqel.art' },
                    { label: '📦 Amazon Appstore', sub: 'Fire TV', url: 'https://www.amazon.com/dp/B0H8DYDWBK/' },
                    { label: '🪟 Microsoft Store', sub: 'Windows', url: 'https://apps.microsoft.com/detail/9PDTLHTGRMDS' },
                    { label: '⬇ Direct download', sub: 'Mac · Windows', url: 'https://github.com/SYQEL/SYQEL-Art-Releases/releases/latest' }
                  ].map((s) => (
                    <a
                      key={s.url}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => { e.preventDefault(); track('download_click', { store: s.label }); openExternal(s.url) }}
                      style={{ fontSize: 11.5, fontFamily: 'monospace', color: '#fff', textDecoration: 'none', padding: '7px 12px', borderRadius: 8, background: 'rgba(124,91,255,0.18)', border: '1px solid rgba(124,91,255,0.5)', textAlign: 'center' }}
                    >
                      {s.label}
                      <span style={{ display: 'block', fontSize: 9.5, opacity: 0.65, marginTop: 2 }}>{s.sub}</span>
                    </a>
                  ))}
                </div>
                <div style={{ marginTop: 12, fontSize: 10.5, opacity: 0.55 }}>
                  📺 on the big screen: <b>Apple TV</b>, <b>Android TV</b> &amp; <b>Fire TV</b> — play on your phone, watch on your TV
                </div>
              </div>
            )}
            <div style={{ marginTop: 26, fontSize: 10.5, display: 'flex', gap: 14, justifyContent: 'center' }}>
              <a href="#privacy" style={footLink}>privacy</a>
              <a href="#terms" style={footLink}>terms</a>
              <a href="#support" style={footLink}>support</a>
              <span style={{ opacity: 0.35 }}>© SYQEL® INC</span>
            </div>
          </div>
        </div>
      )}
      <div
        onMouseEnter={() => { hoverRef.current = true; lastActivityRef.current = performance.now(); setControlsOn(true) }}
        onMouseLeave={() => { hoverRef.current = false }}
        style={{ position: 'absolute', top: 0, right: 0, padding: 12, zIndex: 10 }}
      >
        <div style={{
          width: 230, display: 'flex', flexDirection: 'column', gap: 9, font: '11px monospace',
          color: '#fff', background: 'rgba(0,0,0,0.55)', padding: '10px 12px', borderRadius: 8,
          opacity: controlsOn ? 1 : 0, transition: 'opacity 0.6s ease',
          pointerEvents: controlsOn ? 'auto' : 'none'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ ...wordmark, fontSize: 13, letterSpacing: 2, opacity: 0.9 }}>SYQEL<span style={{ fontSize: 8, verticalAlign: 'super' }}>®</span> Art</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <StarToggle id={currentWorldId} />
              <a href="#gallery" title="worlds gallery" style={{ color: '#fff', textDecoration: 'none', fontSize: 13, opacity: 0.85 }}>⊞</a>
              <button title="remove watermark" onClick={() => setSubReason('support')} style={{ background: 'none', border: 'none', color: '#ff8a3d', cursor: 'pointer', fontSize: 13 }}>🔥</button>
              <button title="fullscreen" onClick={toggleFullscreen} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: 0.85 }}>⛶</button>
            </div>
          </div>
          <SourcePicker />
          {isNativeAudio() && <TvCastPicker />}
          <div style={{ opacity: 0.7, letterSpacing: 0.5 }}>JOURNEY</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
            <input type="checkbox" checked={directed} onChange={(e) => { setDirected(e.target.checked); track('director_toggle', { enabled: e.target.checked }) }} style={{ accentColor: '#7c5bff' }} />
            <span style={{ opacity: 0.85 }}>let the Director drive</span>
          </label>
          {directed && (favList.length > 0 || favOnly) && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
              <input type="checkbox" checked={favOnly} onChange={(e) => { if (e.target.checked) setVolume(FAVORITES_FILTER); else leaveFavorites(); track('favorites_filter', { enabled: e.target.checked, surface: 'director' }) }} style={{ accentColor: '#ffc860' }} />
              <span style={{ opacity: 0.85 }}>★ favorites only</span>
            </label>
          )}
          {!directed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ opacity: 0.7, letterSpacing: 0.5 }}>VISUALS</div>
              <select
                value={volume}
                title="visual volume"
                onChange={(e) => {
                  const v = e.target.value
                  setVolume(v)
                  if (v === FAVORITES_FILTER) {
                    track('favorites_filter', { enabled: true, surface: 'picker' })
                    // stay on the current world if it is a favorite; else land on the newest playable one
                    if (favorites.has(sceneId)) return
                    const fav = favList.find((w) => worldAvailable(w.id))
                    if (fav) { setSceneId(fav.id); track('select_world', { world_id: fav.id, surface: 'picker' }) }
                    return
                  }
                  // land on the first world the user can actually PLAY (free users entering a Pro
                  // volume land on its free showcase — Starfarer in Vol 2 — not a locked world)
                  const first = volumeWorlds(v).find((w) => worldAvailable(w.id)) ?? volumeWorlds(v)[0]
                  if (first) { setSceneId(first.id); track('select_world', { world_id: first.id, surface: 'picker' }) }
                }}
                style={{ background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 6px', font: '11px monospace' }}
              >
                {offeredVolumes().map((v) => <option key={v.id} value={v.id}>{v.short}</option>)}
                {(favList.length > 0 || favOnly) && <option value={FAVORITES_FILTER}>★ Favorites ({favList.length})</option>}
              </select>
              {(pickerWorlds.length > 1 || favOnly) && (
                <select
                  value={sceneId}
                  onChange={(e) => {
                    const id = e.target.value
                    // premium worlds open the paywall instead of switching (the free V2 showcase plays)
                    if (!worldAvailable(id)) { setSubReason('premium'); track('paywall', { surface: 'premium_world', world_id: id }); return }
                    setSceneId(id); track('select_world', { world_id: id, surface: 'picker' })
                    tellIfNotOnTv(id)
                  }}
                  style={{ background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 6px', font: '11px monospace' }}
                >
                  {pickerWorlds.map((w) => (
                    <option key={w.id} value={w.id}>{`${favorites.has(w.id) && !favOnly ? '★ ' : ''}${isNewWorld(w.id) ? 'NEW · ' : ''}${worldAvailable(w.id) ? w.name : `${w.name} ✦ Pro`}${worldNotes(w.id).map((n) => ` · ${n}`).join('')}`}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {/* the selected world is already shown in the dropdown; show this only for the Director's live journey */}
          {directed && <div style={{ opacity: 0.9, fontSize: 12, minHeight: 16 }}>{worldLabel}</div>}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.85 }}>sensitivity</span><span style={{ opacity: 0.55 }}>{sens.toFixed(2)}</span>
            </span>
            <input type="range" min={0.1} max={0.9} step={0.05} value={sens} onChange={(e) => { setSens(parseFloat(e.target.value)); lastActivityRef.current = performance.now() }} style={{ width: '100%', accentColor: '#7c5bff' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.85 }}>brightness</span><span style={{ opacity: 0.55 }}>{exposureLabel(exposure)}</span>
            </span>
            <input type="range" min={-2} max={1} step={0.1} value={exposure} title="double-click to reset" onDoubleClick={() => setExposure(0)} onChange={(e) => { setExposure(Math.round(parseFloat(e.target.value) * 10) / 10); lastActivityRef.current = performance.now() }} style={{ width: '100%', accentColor: '#7c5bff' }} />
          </label>
          <label title="at most 3 flashes a second, on every visual" style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
            <input type="checkbox" checked={calm} onChange={(e) => { setCalm(e.target.checked); lastActivityRef.current = performance.now(); track('calm_toggle', { enabled: e.target.checked }) }} style={{ accentColor: '#7c5bff' }} />
            <span style={{ opacity: 0.85 }}>calm flashes</span>
          </label>
          <label title="on this screen; not shown on TVs" style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
            <input type="checkbox" checked={orb} onChange={(e) => { setOrb(e.target.checked); lastActivityRef.current = performance.now(); track('orb_toggle', { enabled: e.target.checked }) }} style={{ accentColor: '#7c5bff' }} />
            <span style={{ opacity: 0.85 }}>Add orbs that follow the voices</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, opacity: 0.85 }}>
            <span style={{ flex: 1 }}>world updates</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={otaAuto} onChange={(e) => { setOtaAutoState(e.target.checked); persistOtaAuto(e.target.checked) }} />
              auto
            </label>
            {!otaAuto && (
              <button
                onClick={() => { void (async () => { setOtaMsg('checking…'); const n = await checkForUpdatesNow(); if (n > 0) { setOtaMsg('reloading…'); location.reload() } else setOtaMsg('up to date') })() }}
                style={{ background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '2px 8px', font: '10px monospace', cursor: 'pointer' }}
              >check now</button>
            )}
            {otaMsg && <span style={{ fontSize: 10, opacity: 0.6 }}>{otaMsg}</span>}
          </div>
        </div>
      </div>
      {party && <Celebration onDone={() => setParty(false)} />}
      {trainToast && (
        <div style={{
          position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.72)', color: '#9fe8c8', font: '12px monospace',
          padding: '6px 14px', borderRadius: 999, letterSpacing: 1, zIndex: 60, pointerEvents: 'none'
        }}>
          {trainToast}
        </div>
      )}
      {castNotice && (
        <div role="status" data-cast-notice style={{
          position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.72)', color: '#fff', font: '12px monospace',
          padding: '6px 14px', borderRadius: 999, zIndex: 60, pointerEvents: 'none'
        }}>
          📺 {castNotice}
        </div>
      )}
      {subReason && (
        <SubscribeModal
          reason={subReason}
          notice={subNotice}
          onSubscribed={verifySubscribed}
          onClose={() => { setSubNotice(null); setSubReason(null) }}
        />
      )}
    </div>
  )
}

/** ☆/★ — star the visual on screen (SYQEL's favorites symbol; never a heart). Reads the store directly;
 *  the parent re-renders on every favorites change, so this stays current. */
function StarToggle(props: { id: string }): JSX.Element {
  const on = favorites.has(props.id)
  return (
    <button
      title={on ? 'remove from favorites' : 'add to favorites'}
      aria-pressed={on}
      onClick={() => { const now = favorites.toggle(props.id); track('favorite', { world_id: props.id, on: now, surface: 'player' }) }}
      style={{ background: 'none', border: 'none', color: on ? '#ffc860' : '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, opacity: on ? 1 : 0.85 }}
    >{on ? '★' : '☆'}</button>
  )
}
